import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  BodySmallMuted,
  DetailsPageDescription,
  DetailsPageHeader,
  NoSearchResults,
} from '@common/components';
import { ContentParser } from '@common/components/ContentParser';
import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { analytics } from '@features/analytics';
import { DynamicPackagesConfigurationWizard } from '@features/DynamicPackagesConfigurationWizard';
import { OnChangeParams } from '@features/JourneyPicker/JourneyPicker.types';
import { UpgradeItemVersion } from '@features/List/BookableList/UpgradeItem';
import { NotFoundWrapper } from '@features/List/ListPage.styles';
import { useRate } from '@features/rates';
import { useRoomSettings } from '@features/roomSettings';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import rewriteHttp from '@utils/rewriteHttp';
import moment, { Moment } from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'shared-routing';
import { MultiRoomSearch } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import { RateErrorMessage, RoomUpgrade } from 'shared-types/Rate';
import styled from 'styled-components';
import { DatesPicker } from './DatesPicker';
import { RoomsListing } from './RoomsListing';

type PackageDetailProps = {
  journeyParams: ParsedJourneyParams;
  changeJourneyParam: (
    name: string,
    value?: string | Moment | number | MultiRoomSearch | Record<string, number>
  ) => void;
  setJourneyParams: (params: OnChangeParams) => void;
  property: Property;
  language: string;
  errorReason: RateErrorMessage;
  roomIdsInBasket: string[];
  backRoute?: string;
  backText?: string;
};

const PackageDetailWrapper = styled(SectionContainer)`
  margin-top: 10px;
`;

export const PackageDetail: FC<PackageDetailProps> = ({
  journeyParams,
  changeJourneyParam,
  setJourneyParams,
  property,
  language,
  errorReason,
  roomIdsInBasket,
  backRoute,
  backText,
}) => {
  const router = useRouter();
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [hasCompletedFirstFetch, setHasCompletedFirstFetch] = useState(false);

  const errorMessage = useRatesErrorMessage(errorReason);
  const { query } = router;
  const rateCode = query.id as string;
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;
  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const { rate, isFetching } = useRate(
    rateCode,
    {
      fromDate,
      toDate,
      adults,
      children,
      childAges,
      promoRateCode,
      rooms,
    },
    language
  );

  const metaEntity = { type: 'rate', ...rate };

  const { data } = useRoomSettings();
  const roomSettings = data?.data;

  const { room, bookingStep } = query;
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const { t } = useTranslations();
  const pageDescriptionTitle = t('rate.seeRateDetails');
  const noSearchHeading = t('rate.noResults');
  const noSearchButtonText = backText ?? t('app.backToRates');
  const selectedRoom = rate?.rooms?.find((thisRoom) => thisRoom.id === room);
  const upgrade = selectedRoom?.roomUpgrade;

  const checkInTime = roomSettings?.checkInTime;
  const checkOutTime = roomSettings?.checkOutTime;

  function moreRoomsAvailableThanInBasket(room: { [key: string]: any }) {
    if (!room.quantity) return true;
    const numRoomsOfTypeInBasket = roomIdsInBasket.filter((id) => id === room.id).length;
    return numRoomsOfTypeInBasket < room.quantity;
  }

  const availableRooms = (rate?.rooms || []).filter((room) => moreRoomsAvailableThanInBasket(room));
  const allUpgradeRoomsInBasket =
    !!upgrade &&
    !!upgrade.quantity &&
    upgrade.quantity < roomIdsInBasket?.filter((id) => id === upgrade.id)?.length;

  const hasFired = useRef(false);
  const linkParams = { ...query, bookingStep: 'guestDetailsStep' };
  const upgradeLinkParams = { ...linkParams, room: upgrade?.id };

  if (!isFetching && (!availableRooms || !availableRooms.length) && !hasFired.current) {
    analytics.track({
      event: 'denial',
      params: {
        start_date: moment(journeyParams.fromDate).format('YYYY-MM-DD').toString(),
        end_date: moment(journeyParams.toDate).format('YYYY-MM-DD').toString(),
        items: [{ item_category: 'rates', item_id: rate?.id, item_name: rate?.title ?? '' }],
      },
    });
    hasFired.current = true;
  }

  useEffect(() => {
    if (isFetching) {
      hasFired.current = false;
    }
  }, [isFetching]);

  const loadingState = isFetching || isLoadingAvailability;

  const isUpgradeStep = bookingStep === 'upgrade' && !!upgrade;

  const upgradeItem = (
    <UpgradeItemVersion
      isOpen={isUpgradeStep}
      {...(upgrade as RoomUpgrade)}
      linkRoute={Routes.RATE_BOOKING}
      linkParams={linkParams}
      upgradeLinkParams={upgradeLinkParams}
      language={language}
      numberOfNights={moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days')}
      originalRoom={
        selectedRoom
          ? { id: selectedRoom.id, name: selectedRoom.name, imageUrl: selectedRoom.imageUrl }
          : undefined
      }
    />
  );

  if (rate && isEcom2 && isUpgradeStep) {
    return upgradeItem;
  }

  return rate ? (
    <PackageDetailWrapper>
      <DetailsPageHeader
        name={rate.title}
        image={{
          url: rewriteHttp(rate.imageUrl),
        }}
        metadata={{
          price: rate?.price || null,
        }}
      />
      <DetailsPageDescription title={pageDescriptionTitle}>
        <ContentParser text={rate.description} escapeHtml={false} />

        {(checkInTime || checkOutTime) && (
          <p>
            {checkInTime && t('checkout.termsAndConditions.checkIn', { time: checkInTime })}
            {checkInTime && checkOutTime && ' / '}
            {checkOutTime && t('checkout.termsAndConditions.checkOut', { time: checkOutTime })}
          </p>
        )}

        {rate.cancellationPolicyText && (
          <p>
            <ContentParser text={rate.cancellationPolicyText} escapeHtml={false} />
          </p>
        )}
      </DetailsPageDescription>
      <DatesPicker
        isFetching={hasCompletedFirstFetch && loadingState}
        journeyParams={journeyParams}
        rateCode={rate.id}
        showGuestsPicker={true}
        onChangeJourneyParams={(params: OnChangeParams) => {
          const { fromDate, toDate, adults, childAges, children, rooms } = params;

          changeJourneyParam('fromDate', fromDate);
          changeJourneyParam('toDate', toDate);
          changeJourneyParam('spaDate', fromDate);
          changeJourneyParam('adults', adults);
          changeJourneyParam('children', children);
          changeJourneyParam('rooms', rooms);
          changeJourneyParam('childAges', childAges);
          setJourneyParams(params);
        }}
      />
      {errorReason ? (
        <NotFoundWrapper>
          {errorMessage.title}
          <br />
          <BodySmallMuted>{errorMessage.message}</BodySmallMuted>
        </NotFoundWrapper>
      ) : (
        <RoomsListing
          rate={{
            ...rate,
            name: rate.title,
            fromPrice: rate.price,
            rooms: availableRooms,
          }}
          journeyParams={journeyParams}
          isFetching={isFetching || isLoadingAvailability}
          allUpgradeRoomsInBasket={allUpgradeRoomsInBasket}
          property={property}
          setIsLoadingAvailability={setIsLoadingAvailability}
          setHasCompletedFirstFetch={setHasCompletedFirstFetch}
          hasCompletedFirstFetch={hasCompletedFirstFetch}
        />
      )}
      {bookingStep && (
        <DynamicPackagesConfigurationWizard
          language={language}
          rate={metaEntity as any}
          room={{ type: 'room', id: room as string }}
        />
      )}
      {upgradeItem}
    </PackageDetailWrapper>
  ) : (
    <NoSearchResults
      headingText={noSearchHeading}
      linkRoute={backRoute ?? Routes.RATE_LIST}
      linkText={noSearchButtonText}
    />
  );
};
